import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { TabView, TabPanel } from "primereact/tabview";
import { Tag } from "primereact/tag";
import { Badge } from "primereact/badge";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Divider } from "primereact/divider";
import { ProgressBar } from "primereact/progressbar";

import Layout from "../../component/layout";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { useFacilityStore } from "../../../../app/z-store/store";

const AssetManagement = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Asset Modal State
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetFormData, setAssetFormData] = useState({
    name: "",
    location: "",
    serialNumber: "",
    dateBought: "",
    insuranceStatus: "",
  });

  // Document Modal State
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentFormData, setDocumentFormData] = useState({
    name: "",
    type: "",
    url: "",
  });
  const [selectedAssetForDoc, setSelectedAssetForDoc] = useState(null);

  // Inspection Modal State
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [inspectionFormData, setInspectionFormData] = useState({
    dateInspected: "",
    status: "",
    certificate: "",
  });
  const [selectedAssetForInspection, setSelectedAssetForInspection] =
    useState(null);

  // Statistics State
  const [assetStats, setAssetStats] = useState({
    totalAssets: 0,
    insuredAssets: 0,
    uninsuredAssets: 0,
    assignedAssets: 0,
    unassignedAssets: 0,
  });
  const [inspectionStats, setInspectionStats] = useState({});
  const [documentStats, setDocumentStats] = useState({});
  const [inspectionAlerts, setInspectionAlerts] = useState([]);

  const toast = useRef(null);
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  // Dropdown options
  const insuranceOptions = [
    { label: "Insured", value: "Insured" },
    { label: "Not Insured", value: "Not Insured" },
    { label: "Expired", value: "Expired" },
  ];

  const documentTypeOptions = [
    { label: "PDF", value: "PDF" },
    { label: "Image", value: "Image" },
    { label: "Video", value: "Video" },
  ];

  const inspectionStatusOptions = [
    { label: "Passed", value: "Passed" },
    { label: "Failed", value: "Failed" },
    { label: "Pending", value: "Pending" },
  ];

  useEffect(() => {
    if (selectedFacilityId) {
      fetchAssets();
      fetchInspectionStats();
      fetchDocumentStats();
      fetchInspectionAlerts();
    }
  }, [selectedFacilityId]);

  // API calls
  const fetchAssets = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/assets/${selectedFacilityId}`,
        "GET",
        null,
      );

      if (response.success) {
        const assetData = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data.assets)
            ? response.data.assets
            : [];
        setAssets(assetData);
        calculateStats(assetData);
        setError(null);
      } else {
        setError(response.error);
        showToast("error", "Error", response.error);
      }
    } catch (err) {
      setError("Failed to fetch assets");
      showToast("error", "Error", err.message || "Failed to fetch assets");
    } finally {
      setLoading(false);
    }
  };

  const fetchInspectionStats = async () => {
    try {
      const response = await makeRequest2(
        `/api/assets/${selectedFacilityId}/inspection-statistics`,
        "GET",
        null,
      );
      if (response.success) {
        setInspectionStats(response.data.statistics || {});
      }
    } catch (err) {
      console.error("Failed to fetch inspection stats:", err);
    }
  };

  const fetchDocumentStats = async () => {
    try {
      const response = await makeRequest2(
        `/api/assets/${selectedFacilityId}/document-statistics`,
        "GET",
        null,
      );
      if (response.success) {
        setDocumentStats(response.data.statistics || {});
      }
    } catch (err) {
      console.error("Failed to fetch document stats:", err);
    }
  };

  const fetchInspectionAlerts = async () => {
    try {
      const response = await makeRequest2(
        `/api/assets/${selectedFacilityId}/inspection-alerts`,
        "GET",
        null,
      );
      if (response.success) {
        setInspectionAlerts(response.data.alerts || []);
      }
    } catch (err) {
      console.error("Failed to fetch inspection alerts:", err);
    }
  };

  // Calculate asset statistics
  const calculateStats = (assetData) => {
    const totalAssets = assetData.length;
    const insuredAssets = assetData.filter(
      (asset) => asset.insuranceStatus === "Insured",
    ).length;
    const uninsuredAssets = assetData.filter(
      (asset) => asset.insuranceStatus === "Not Insured",
    ).length;
    const assignedAssets = assetData.filter(
      (asset) => asset.assigned === true,
    ).length;
    const unassignedAssets = totalAssets - assignedAssets;

    setAssetStats({
      totalAssets,
      insuredAssets,
      uninsuredAssets,
      assignedAssets,
      unassignedAssets,
    });
  };

  const showToast = (severity, summary, detail) => {
    toast.current.show({ severity, summary, detail });
  };

  const filteredAssets = Array.isArray(assets)
    ? assets.filter((asset) =>
        Object.values(asset).some((value) =>
          value?.toString().toLowerCase().includes(searchKeyword.toLowerCase()),
        ),
      )
    : [];

  // Asset handlers
  const handleAssetInputChange = (e) => {
    setAssetFormData({
      ...assetFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAssetModalOpen = (asset = null) => {
    if (asset) {
      setSelectedAsset(asset);
      setAssetFormData({
        name: asset.name,
        location: asset.location || "",
        serialNumber: asset.serialNumber,
        dateBought: asset.dateBought.split("T")[0],
        insuranceStatus: asset.insuranceStatus,
      });
    } else {
      setSelectedAsset(null);
      setAssetFormData({
        name: "",
        location: "",
        serialNumber: "",
        dateBought: "",
        insuranceStatus: "",
      });
    }
    setShowAssetModal(true);
  };

  const handleAssetSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let response;
      if (selectedAsset) {
        response = await makeRequest2(
          `/api/assets/${selectedFacilityId}/${selectedAsset._id}`,
          "PUT",
          assetFormData,
        );
      } else {
        response = await makeRequest2(
          `/api/assets/${selectedFacilityId}`,
          "POST",
          assetFormData,
        );
      }

      if (response.success) {
        showToast(
          "success",
          "Success",
          selectedAsset
            ? "Asset updated successfully"
            : "Asset added successfully",
        );
        fetchAssets();
        setShowAssetModal(false);
      } else {
        showToast("error", "Error", response.error);
      }
    } catch (err) {
      showToast("error", "Error", "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Document handlers
  const handleDocumentModalOpen = (asset) => {
    setSelectedAssetForDoc(asset);
    setDocumentFormData({ name: "", type: "", url: "" });
    setShowDocumentModal(true);
  };

  const handleDocumentSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await makeRequest2(
        `/api/assets/${selectedFacilityId}/${selectedAssetForDoc._id}/document`,
        "POST",
        documentFormData,
      );

      if (response.success) {
        showToast("success", "Success", "Document added successfully");
        fetchAssets();
        fetchDocumentStats();
        setShowDocumentModal(false);
      } else {
        showToast("error", "Error", response.error);
      }
    } catch (err) {
      showToast("error", "Error", "Failed to add document");
    } finally {
      setLoading(false);
    }
  };

  // Inspection handlers
  const handleInspectionModalOpen = (asset) => {
    setSelectedAssetForInspection(asset);
    setInspectionFormData({ dateInspected: "", status: "", certificate: "" });
    setShowInspectionModal(true);
  };

  const handleInspectionSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await makeRequest2(
        `/api/assets/${selectedFacilityId}/${selectedAssetForInspection._id}/inspection-certificate`,
        "POST",
        inspectionFormData,
      );

      if (response.success) {
        showToast(
          "success",
          "Success",
          "Inspection certificate added successfully",
        );
        fetchAssets();
        fetchInspectionStats();
        fetchInspectionAlerts();
        setShowInspectionModal(false);
      } else {
        showToast("error", "Error", response.error);
      }
    } catch (err) {
      showToast("error", "Error", "Failed to add inspection certificate");
    } finally {
      setLoading(false);
    }
  };

  const toggleAssignmentStatus = async (asset) => {
    try {
      const response = await makeRequest2(
        `/api/assets/${selectedFacilityId}/${asset._id}/assign-status`,
        "PUT",
        { assigned: !asset.assigned },
      );

      if (response.success) {
        showToast(
          "success",
          "Success",
          `Asset ${!asset.assigned ? "assigned" : "unassigned"} successfully`,
        );
        fetchAssets();
      } else {
        showToast("error", "Error", response.error);
      }
    } catch (err) {
      showToast("error", "Error", "Failed to update assignment status");
    }
  };

  const handleDeleteAsset = (asset) => {
    confirmDialog({
      message: `Are you sure you want to delete asset "${asset.name}"?`,
      header: "Delete Confirmation",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          const response = await makeRequest2(
            `/api/assets/${selectedFacilityId}/${asset._id}`,
            "DELETE",
            null,
          );

          if (response.success) {
            showToast("success", "Success", "Asset deleted successfully");
            fetchAssets();
          } else {
            showToast("error", "Error", response.error);
          }
        } catch (err) {
          showToast("error", "Error", "Failed to delete asset");
        }
      },
    });
  };

  // Column templates
  const insuranceStatusTemplate = (rowData) => {
    const getSeverity = (status) => {
      switch (status) {
        case "Insured":
          return "success";
        case "Expired":
          return "warning";
        case "Not Insured":
          return "danger";
        default:
          return "info";
      }
    };
    return (
      <Tag
        value={rowData.insuranceStatus}
        severity={getSeverity(rowData.insuranceStatus)}
        className="font-semibold"
      />
    );
  };

  const assignmentStatusTemplate = (rowData) => {
    return (
      <Tag
        value={rowData.assigned ? "Assigned" : "Available"}
        severity={rowData.assigned ? "info" : "success"}
        className="font-semibold"
      />
    );
  };

  const inspectionStatusTemplate = (rowData) => {
    if (
      !rowData.inspection_certificate ||
      rowData.inspection_certificate.length === 0
    ) {
      return (
        <Tag
          value="No Inspection"
          severity="contrast"
          className="font-semibold"
        />
      );
    }

    const latestInspection = rowData.inspection_certificate[0];
    const getSeverity = (status) => {
      switch (status) {
        case "Passed":
          return "success";
        case "Failed":
          return "danger";
        case "Pending":
          return "warning";
        default:
          return "info";
      }
    };

    return (
      <Tag
        value={latestInspection.status}
        severity={getSeverity(latestInspection.status)}
        className="font-semibold"
      />
    );
  };

  const documentsTemplate = (rowData) => {
    const docCount = rowData.documents ? rowData.documents.length : 0;
    return (
      <div className="d-flex align-items-center gap-2">
        <Badge
          value={docCount}
          severity={docCount > 0 ? "success" : "secondary"}
        />
        <small className="text-muted">
          {docCount} doc{docCount !== 1 ? "s" : ""}
        </small>
      </div>
    );
  };

  const actionTemplate = (rowData) => {
    return (
      <div className="d-flex gap-1 flex-wrap">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-outlined p-button-info"
          size="small"
          onClick={() => handleAssetModalOpen(rowData)}
          tooltip="Edit Asset"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-file-plus"
          className="p-button-rounded p-button-outlined p-button-success"
          size="small"
          onClick={() => handleDocumentModalOpen(rowData)}
          tooltip="Add Document"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-shield"
          className="p-button-rounded p-button-outlined p-button-warning"
          size="small"
          onClick={() => handleInspectionModalOpen(rowData)}
          tooltip="Add Inspection"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon={rowData.assigned ? "pi pi-unlock" : "pi pi-lock"}
          className={`p-button-rounded p-button-outlined ${rowData.assigned ? "p-button-secondary" : "p-button-primary"}`}
          size="small"
          onClick={() => toggleAssignmentStatus(rowData)}
          tooltip={rowData.assigned ? "Unassign" : "Assign"}
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-outlined p-button-danger"
          size="small"
          onClick={() => handleDeleteAsset(rowData)}
          tooltip="Delete Asset"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  const alertActionTemplate = (rowData) => {
    return (
      <Button
        icon="pi pi-shield"
        label="Add Inspection"
        className="p-button-warning p-button-sm"
        onClick={() => handleInspectionModalOpen(rowData.asset)}
      />
    );
  };

  const alertTypeTemplate = (rowData) => {
    const getSeverity = (type) => {
      switch (type) {
        case "FAILED":
          return "danger";
        case "NO_CERTIFICATES":
          return "danger";
        case "PENDING":
          return "warning";
        case "INSPECTION_OVERDUE":
          return "warning";
        default:
          return "info";
      }
    };

    const getLabel = (type) => {
      switch (type) {
        case "FAILED":
          return "Failed Inspection";
        case "NO_CERTIFICATES":
          return "No Certificates";
        case "PENDING":
          return "Pending Inspection";
        case "INSPECTION_OVERDUE":
          return "Overdue Inspection";
        default:
          return type;
      }
    };

    return (
      <Tag
        value={getLabel(rowData.alertType)}
        severity={getSeverity(rowData.alertType)}
        className="font-semibold"
      />
    );
  };

  const renderHeader = () => {
    return (
      <div className="d-flex justify-content-between align-items-center p-3 bg-light border-bottom">
        <div className="d-flex align-items-center gap-3">
          <i className="pi pi-search text-muted"></i>
          <InputText
            type="text"
            placeholder="Search assets..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            className="p-inputtext-sm"
            style={{ width: "300px" }}
          />
        </div>
        <Button
          label="Add New Asset"
          icon="pi pi-plus"
          onClick={() => handleAssetModalOpen()}
          className="p-button-primary"
        />
      </div>
    );
  };

  // Statistics cards component
  const StatisticsCards = () => {
    if (loading) {
      return (
        <div className="row mb-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="col-md-3 col-sm-6 mb-3">
              <div className="card h-100">
                <div className="card-body">
                  <Skeleton height="60px" />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="row mb-4">
        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card bg-primary text-white h-100 shadow-sm">
            <div className="card-body d-flex align-items-center">
              <div className="flex-grow-1">
                <h2 className="mb-0 text-white">{assetStats.totalAssets}</h2>
                <p className="mb-0 text-white-50">Total Assets</p>
              </div>
              <div className="ms-3">
                <i
                  className="pi pi-home"
                  style={{ fontSize: "2rem", opacity: 0.7 }}
                ></i>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card bg-success text-white h-100 shadow-sm">
            <div className="card-body d-flex align-items-center">
              <div className="flex-grow-1">
                <h2 className="mb-0 text-white">{assetStats.insuredAssets}</h2>
                <p className="mb-0 text-white-50">Insured Assets</p>
              </div>
              <div className="ms-3">
                <i
                  className="pi pi-shield"
                  style={{ fontSize: "2rem", opacity: 0.7 }}
                ></i>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card bg-warning text-white h-100 shadow-sm">
            <div className="card-body d-flex align-items-center">
              <div className="flex-grow-1">
                <h2 className="mb-0 text-white">{assetStats.assignedAssets}</h2>
                <p className="mb-0 text-white-50">Assigned Assets</p>
              </div>
              <div className="ms-3">
                <i
                  className="pi pi-user"
                  style={{ fontSize: "2rem", opacity: 0.7 }}
                ></i>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3 col-sm-6 mb-3">
          <div className="card bg-danger text-white h-100 shadow-sm">
            <div className="card-body d-flex align-items-center">
              <div className="flex-grow-1">
                <h2 className="mb-0 text-white">{inspectionAlerts.length}</h2>
                <p className="mb-0 text-white-50">Inspection Alerts</p>
              </div>
              <div className="ms-3">
                <i
                  className="pi pi-exclamation-triangle"
                  style={{ fontSize: "2rem", opacity: 0.7 }}
                ></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <style jsx>{`
        .p-datatable .p-datatable-tbody > tr > td {
          padding: 0.75rem;
          border-color: #e9ecef;
        }

        .p-datatable .p-datatable-thead > tr > th {
          padding: 1rem 0.75rem;
          background: #f8f9fa;
          border-color: #dee2e6;
          font-weight: 600;
          color: #495057;
        }

        .p-tabview .p-tabview-nav li .p-tabview-nav-link {
          padding: 1rem 1.5rem;
          font-weight: 500;
        }

        .statistics-card {
          transition: transform 0.2s ease-in-out;
        }

        .statistics-card:hover {
          transform: translateY(-2px);
        }

        .action-buttons .p-button {
          transition: all 0.2s ease-in-out;
        }

        .action-buttons .p-button:hover {
          transform: scale(1.05);
        }

        .p-dialog .p-dialog-header {
          background: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
        }

        .p-dialog .p-dialog-content {
          padding: 2rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          font-weight: 500;
          color: #495057;
          margin-bottom: 0.5rem;
          display: block;
        }

        .p-card {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          border-radius: 8px;
        }

        .p-card .p-card-body {
          padding: 1.5rem;
        }
      `}</style>

      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={"/facility/"} className="text-decoration-none">
                      <i className="pi pi-home me-1"></i>
                      Dashboard
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Asset Management
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="container-fluid">
        <StatisticsCards />

        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-body p-0">
                <TabView
                  activeIndex={activeTabIndex}
                  onTabChange={(e) => setActiveTabIndex(e.index)}
                  className="custom-tabview"
                >
                  {/* Assets Tab */}
                  <TabPanel header="Assets" leftIcon="pi pi-home me-2">
                    <div className="p-3">
                      <DataTable
                        value={filteredAssets}
                        emptyMessage="No assets found."
                        sortMode="multiple"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        stripedRows
                        loading={loading}
                        header={renderHeader}
                        className="p-datatable-striped"
                        responsiveLayout="scroll"
                        breakpoint="960px"
                      >
                        <Column
                          field="name"
                          header="Asset Name"
                          sortable
                          className="font-semibold"
                        />
                        <Column field="location" header="Location" sortable />
                        <Column
                          field="serialNumber"
                          header="Serial Number"
                          sortable
                        />
                        <Column
                          field="dateBought"
                          header="Date Bought"
                          sortable
                          body={(rowData) => (
                            <span className="text-muted">
                              {new Date(rowData.dateBought).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                },
                              )}
                            </span>
                          )}
                        />
                        <Column
                          field="insuranceStatus"
                          header="Insurance"
                          body={insuranceStatusTemplate}
                          sortable
                        />
                        <Column
                          field="assigned"
                          header="Assignment"
                          body={assignmentStatusTemplate}
                          sortable
                        />
                        <Column
                          header="Inspection"
                          body={inspectionStatusTemplate}
                        />
                        <Column header="Documents" body={documentsTemplate} />
                        <Column
                          body={actionTemplate}
                          header="Actions"
                          className="action-buttons"
                          style={{ minWidth: "280px" }}
                        />
                      </DataTable>
                    </div>
                  </TabPanel>

                  {/* Inspection Alerts Tab */}
                  <TabPanel
                    header={`Inspection Alerts ${inspectionAlerts.length > 0 ? `(${inspectionAlerts.length})` : ""}`}
                    leftIcon="pi pi-exclamation-triangle me-2"
                  >
                    <div className="p-3">
                      {inspectionAlerts.length > 0 ? (
                        <DataTable
                          value={inspectionAlerts}
                          emptyMessage="No inspection alerts."
                          paginator
                          rows={10}
                          stripedRows
                          className="p-datatable-striped"
                          responsiveLayout="scroll"
                        >
                          <Column
                            field="asset.name"
                            header="Asset Name"
                            sortable
                            className="font-semibold"
                          />
                          <Column
                            field="asset.location"
                            header="Location"
                            sortable
                          />
                          <Column
                            field="asset.serialNumber"
                            header="Serial Number"
                            sortable
                          />
                          <Column
                            field="alertType"
                            header="Alert Type"
                            body={alertTypeTemplate}
                            sortable
                          />
                          <Column
                            field="daysSinceInspection"
                            header="Days Since Inspection"
                            sortable
                            body={(rowData) => (
                              <span
                                className={
                                  rowData.daysSinceInspection > 365
                                    ? "text-danger fw-bold"
                                    : "text-muted"
                                }
                              >
                                {rowData.daysSinceInspection || "N/A"}
                              </span>
                            )}
                          />
                          <Column body={alertActionTemplate} header="Actions" />
                        </DataTable>
                      ) : (
                        <div className="text-center py-5">
                          <i
                            className="pi pi-check-circle text-success"
                            style={{ fontSize: "3rem" }}
                          ></i>
                          <h4 className="mt-3 text-success">All Clear!</h4>
                          <p className="text-muted">
                            No inspection alerts at this time.
                          </p>
                        </div>
                      )}
                    </div>
                  </TabPanel>

                  {/* Statistics Tab */}
                  <TabPanel header="Statistics" leftIcon="pi pi-chart-bar me-2">
                    <div className="p-4">
                      <div className="row">
                        {/* Asset Overview */}
                        <div className="col-md-6 col-lg-4 mb-4">
                          <Card className="h-100 statistics-card">
                            <h5 className="card-title d-flex align-items-center mb-3">
                              <i className="pi pi-home text-primary me-2"></i>
                              Asset Overview
                            </h5>
                            <div className="row text-center">
                              <div className="col-6">
                                <div className="border-end">
                                  <h3 className="text-primary mb-1">
                                    {assetStats.totalAssets}
                                  </h3>
                                  <small className="text-muted">
                                    Total Assets
                                  </small>
                                </div>
                              </div>
                              <div className="col-6">
                                <h3 className="text-warning mb-1">
                                  {assetStats.assignedAssets}
                                </h3>
                                <small className="text-muted">Assigned</small>
                              </div>
                            </div>
                          </Card>
                        </div>

                        {/* Insurance Status */}
                        <div className="col-md-6 col-lg-4 mb-4">
                          <Card className="h-100 statistics-card">
                            <h5 className="card-title d-flex align-items-center mb-3">
                              <i className="pi pi-shield text-success me-2"></i>
                              Insurance Status
                            </h5>
                            <div className="row text-center">
                              <div className="col-4">
                                <h4 className="text-success mb-1">
                                  {assetStats.insuredAssets}
                                </h4>
                                <small className="text-muted">Insured</small>
                              </div>
                              <div className="col-4">
                                <h4 className="text-danger mb-1">
                                  {assetStats.uninsuredAssets}
                                </h4>
                                <small className="text-muted">Uninsured</small>
                              </div>
                              <div className="col-4">
                                <h4 className="text-warning mb-1">
                                  {
                                    assets.filter(
                                      (a) => a.insuranceStatus === "Expired",
                                    ).length
                                  }
                                </h4>
                                <small className="text-muted">Expired</small>
                              </div>
                            </div>
                            <div className="mt-3">
                              <ProgressBar
                                value={
                                  assetStats.totalAssets > 0
                                    ? (assetStats.insuredAssets /
                                        assetStats.totalAssets) *
                                      100
                                    : 0
                                }
                                className="mb-2"
                                style={{ height: "8px" }}
                              />
                              <small className="text-muted">
                                {assetStats.totalAssets > 0
                                  ? `${Math.round((assetStats.insuredAssets / assetStats.totalAssets) * 100)}% Insured`
                                  : "No data"}
                              </small>
                            </div>
                          </Card>
                        </div>

                        {/* Inspection Stats */}
                        {inspectionStats.totalAssets ? (
                          <div className="col-md-6 col-lg-4 mb-4">
                            <Card className="h-100 statistics-card">
                              <h5 className="card-title d-flex align-items-center mb-3">
                                <i className="pi pi-verified text-info me-2"></i>
                                Inspection Status
                              </h5>
                              <div className="row text-center">
                                <div className="col-6">
                                  <div className="border-end">
                                    <h4 className="text-success mb-1">
                                      {inspectionStats
                                        .certificateStatusBreakdown?.passed ||
                                        0}
                                    </h4>
                                    <small className="text-muted">Passed</small>
                                  </div>
                                </div>
                                <div className="col-6">
                                  <h4 className="text-danger mb-1">
                                    {inspectionStats.certificateStatusBreakdown
                                      ?.failed || 0}
                                  </h4>
                                  <small className="text-muted">Failed</small>
                                </div>
                              </div>
                              <div className="mt-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <small className="text-muted">
                                    Compliance Rate
                                  </small>
                                  <Badge
                                    value={`${inspectionStats.complianceRate || 0}%`}
                                    severity="info"
                                  />
                                </div>
                                <ProgressBar
                                  value={parseFloat(
                                    inspectionStats.complianceRate || 0,
                                  )}
                                  className="mb-2"
                                  style={{ height: "8px" }}
                                />
                              </div>
                            </Card>
                          </div>
                        ) : (
                          <div className="col-md-6 col-lg-4 mb-4">
                            <Card className="h-100 statistics-card">
                              <div className="text-center py-4">
                                <i
                                  className="pi pi-info-circle text-muted"
                                  style={{ fontSize: "2rem" }}
                                ></i>
                                <p className="text-muted mt-2">
                                  No inspection data available
                                </p>
                              </div>
                            </Card>
                          </div>
                        )}

                        {/* Document Stats */}
                        {documentStats.totalAssets ? (
                          <div className="col-md-6 col-lg-4 mb-4">
                            <Card className="h-100 statistics-card">
                              <h5 className="card-title d-flex align-items-center mb-3">
                                <i className="pi pi-file text-secondary me-2"></i>
                                Documentation
                              </h5>
                              <div className="row text-center">
                                <div className="col-6">
                                  <div className="border-end">
                                    <h4 className="text-primary mb-1">
                                      {documentStats.totalDocuments || 0}
                                    </h4>
                                    <small className="text-muted">
                                      Total Docs
                                    </small>
                                  </div>
                                </div>
                                <div className="col-6">
                                  <h4 className="text-success mb-1">
                                    {documentStats.documentationCoverage || 0}%
                                  </h4>
                                  <small className="text-muted">Coverage</small>
                                </div>
                              </div>
                              <Divider />
                              <div className="row text-center">
                                <div className="col-4">
                                  <div className="text-danger mb-1">
                                    <i
                                      className="pi pi-file-pdf"
                                      style={{ fontSize: "1.2rem" }}
                                    ></i>
                                  </div>
                                  <div className="fw-bold">
                                    {documentStats.documentTypeBreakdown?.pdf ||
                                      0}
                                  </div>
                                  <small className="text-muted">PDFs</small>
                                </div>
                                <div className="col-4">
                                  <div className="text-info mb-1">
                                    <i
                                      className="pi pi-image"
                                      style={{ fontSize: "1.2rem" }}
                                    ></i>
                                  </div>
                                  <div className="fw-bold">
                                    {documentStats.documentTypeBreakdown
                                      ?.image || 0}
                                  </div>
                                  <small className="text-muted">Images</small>
                                </div>
                                <div className="col-4">
                                  <div className="text-warning mb-1">
                                    <i
                                      className="pi pi-video"
                                      style={{ fontSize: "1.2rem" }}
                                    ></i>
                                  </div>
                                  <div className="fw-bold">
                                    {documentStats.documentTypeBreakdown
                                      ?.video || 0}
                                  </div>
                                  <small className="text-muted">Videos</small>
                                </div>
                              </div>
                            </Card>
                          </div>
                        ) : (
                          <div className="col-md-6 col-lg-4 mb-4">
                            <Card className="h-100 statistics-card">
                              <div className="text-center py-4">
                                <i
                                  className="pi pi-file text-muted"
                                  style={{ fontSize: "2rem" }}
                                ></i>
                                <p className="text-muted mt-2">
                                  No document data available
                                </p>
                              </div>
                            </Card>
                          </div>
                        )}

                        {/* Quick Actions */}
                        <div className="col-md-6 col-lg-4 mb-4">
                          <Card className="h-100 statistics-card">
                            <h5 className="card-title d-flex align-items-center mb-3">
                              <i className="pi pi-cog text-dark me-2"></i>
                              Quick Actions
                            </h5>
                            <div className="d-grid gap-2">
                              <Button
                                label="Add New Asset"
                                icon="pi pi-plus"
                                onClick={() => handleAssetModalOpen()}
                                className="p-button-primary"
                              />
                              <Button
                                label="Export Assets"
                                icon="pi pi-download"
                                className="p-button-outlined p-button-secondary"
                                onClick={() =>
                                  showToast(
                                    "info",
                                    "Export",
                                    "Export functionality coming soon",
                                  )
                                }
                              />
                              <Button
                                label="Bulk Operations"
                                icon="pi pi-list"
                                className="p-button-outlined p-button-secondary"
                                onClick={() =>
                                  showToast(
                                    "info",
                                    "Bulk Operations",
                                    "Bulk operations coming soon",
                                  )
                                }
                              />
                            </div>
                          </Card>
                        </div>
                      </div>
                    </div>
                  </TabPanel>
                </TabView>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Asset Add/Edit Modal */}
      <Dialog
        header={
          <div className="d-flex align-items-center">
            <i
              className={`pi ${selectedAsset ? "pi-pencil" : "pi-plus"} me-2`}
            ></i>
            {selectedAsset ? "Edit Asset" : "Add New Asset"}
          </div>
        }
        visible={showAssetModal}
        onHide={() => setShowAssetModal(false)}
        style={{ width: "500px" }}
        breakpoints={{ "960px": "75vw", "641px": "95vw" }}
        modal
        className="p-fluid"
      >
        <form onSubmit={handleAssetSubmit}>
          <div className="row">
            <div className="col-12 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-tag me-1"></i>
                  Asset Name
                </label>
                <InputText
                  name="name"
                  value={assetFormData.name}
                  onChange={handleAssetInputChange}
                  required
                  className="form-control"
                  placeholder="Enter asset name"
                />
              </div>
            </div>

            <div className="col-12 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-map-marker me-1"></i>
                  Location
                </label>
                <InputText
                  name="location"
                  value={assetFormData.location}
                  onChange={handleAssetInputChange}
                  required
                  className="form-control"
                  placeholder="Enter asset location"
                />
              </div>
            </div>

            <div className="col-12 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-id-card me-1"></i>
                  Serial Number
                </label>
                <InputText
                  name="serialNumber"
                  value={assetFormData.serialNumber}
                  onChange={handleAssetInputChange}
                  required
                  className="form-control"
                  placeholder="Enter serial number"
                />
              </div>
            </div>

            <div className="col-12 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-calendar me-1"></i>
                  Date Bought
                </label>
                <InputText
                  type="date"
                  name="dateBought"
                  value={assetFormData.dateBought}
                  onChange={handleAssetInputChange}
                  required
                  className="form-control"
                />
              </div>
            </div>

            <div className="col-12 mb-4">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-shield me-1"></i>
                  Insurance Status
                </label>
                <Dropdown
                  name="insuranceStatus"
                  value={assetFormData.insuranceStatus}
                  onChange={(e) =>
                    setAssetFormData({
                      ...assetFormData,
                      insuranceStatus: e.value,
                    })
                  }
                  options={insuranceOptions}
                  placeholder="Select Insurance Status"
                  required
                  className="w-100"
                />
              </div>
            </div>
          </div>

          <Divider />

          <div className="d-flex justify-content-end gap-2 pt-3">
            <Button
              type="button"
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowAssetModal(false)}
              className="p-button-outlined p-button-secondary"
            />
            <Button
              type="submit"
              label={selectedAsset ? "Update Asset" : "Add Asset"}
              icon={selectedAsset ? "pi pi-check" : "pi pi-plus"}
              loading={loading}
              className="p-button-primary"
            />
          </div>
        </form>
      </Dialog>

      {/* Document Add Modal */}
      <Dialog
        header={
          <div className="d-flex align-items-center">
            <i className="pi pi-file-plus me-2"></i>
            Add Document -{" "}
            <span className="text-primary">{selectedAssetForDoc?.name}</span>
          </div>
        }
        visible={showDocumentModal}
        onHide={() => setShowDocumentModal(false)}
        style={{ width: "500px" }}
        breakpoints={{ "960px": "75vw", "641px": "95vw" }}
        modal
        className="p-fluid"
      >
        <div className="alert alert-info d-flex align-items-center mb-3">
          <i className="pi pi-info-circle me-2"></i>
          <div>
            <small>
              Adding document to: <strong>{selectedAssetForDoc?.name}</strong>
            </small>
            <br />
            <small className="text-muted">
              Serial: {selectedAssetForDoc?.serialNumber}
            </small>
          </div>
        </div>

        <form onSubmit={handleDocumentSubmit}>
          <div className="row">
            <div className="col-12 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-file me-1"></i>
                  Document Name
                </label>
                <InputText
                  name="name"
                  value={documentFormData.name}
                  onChange={(e) =>
                    setDocumentFormData({
                      ...documentFormData,
                      name: e.target.value,
                    })
                  }
                  required
                  className="form-control"
                  placeholder="e.g., Purchase Receipt, Manual, Warranty"
                />
              </div>
            </div>

            <div className="col-12 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-folder me-1"></i>
                  Document Type
                </label>
                <Dropdown
                  name="type"
                  value={documentFormData.type}
                  onChange={(e) =>
                    setDocumentFormData({ ...documentFormData, type: e.value })
                  }
                  options={documentTypeOptions}
                  placeholder="Select Document Type"
                  required
                  className="w-100"
                />
              </div>
            </div>

            <div className="col-12 mb-4">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-link me-1"></i>
                  Document URL/Path
                </label>
                <InputText
                  name="url"
                  value={documentFormData.url}
                  onChange={(e) =>
                    setDocumentFormData({
                      ...documentFormData,
                      url: e.target.value,
                    })
                  }
                  required
                  className="form-control"
                  placeholder="https://example.com/document.pdf or /path/to/file"
                />
              </div>
            </div>
          </div>

          <Divider />

          <div className="d-flex justify-content-end gap-2 pt-3">
            <Button
              type="button"
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowDocumentModal(false)}
              className="p-button-outlined p-button-secondary"
            />
            <Button
              type="submit"
              label="Add Document"
              icon="pi pi-upload"
              loading={loading}
              className="p-button-success"
            />
          </div>
        </form>
      </Dialog>

      {/* Inspection Certificate Add Modal */}
      <Dialog
        header={
          <div className="d-flex align-items-center">
            <i className="pi pi-shield me-2"></i>
            Add Inspection Certificate -{" "}
            <span className="text-primary">
              {selectedAssetForInspection?.name}
            </span>
          </div>
        }
        visible={showInspectionModal}
        onHide={() => setShowInspectionModal(false)}
        style={{ width: "500px" }}
        breakpoints={{ "960px": "75vw", "641px": "95vw" }}
        modal
        className="p-fluid"
      >
        <div className="alert alert-warning d-flex align-items-center mb-3">
          <i className="pi pi-exclamation-triangle me-2"></i>
          <div>
            <small>
              Adding inspection certificate to:{" "}
              <strong>{selectedAssetForInspection?.name}</strong>
            </small>
            <br />
            <small className="text-muted">
              Location: {selectedAssetForInspection?.location}
            </small>
          </div>
        </div>

        <form onSubmit={handleInspectionSubmit}>
          <div className="row">
            <div className="col-12 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-calendar me-1"></i>
                  Date Inspected
                </label>
                <InputText
                  type="date"
                  name="dateInspected"
                  value={inspectionFormData.dateInspected}
                  onChange={(e) =>
                    setInspectionFormData({
                      ...inspectionFormData,
                      dateInspected: e.target.value,
                    })
                  }
                  required
                  className="form-control"
                  max={new Date().toISOString().split("T")[0]}
                />
                <small className="text-muted">
                  Inspection date cannot be in the future
                </small>
              </div>
            </div>

            <div className="col-12 mb-3">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-check-circle me-1"></i>
                  Inspection Status
                </label>
                <Dropdown
                  name="status"
                  value={inspectionFormData.status}
                  onChange={(e) =>
                    setInspectionFormData({
                      ...inspectionFormData,
                      status: e.value,
                    })
                  }
                  options={inspectionStatusOptions}
                  placeholder="Select Inspection Result"
                  required
                  className="w-100"
                />
              </div>
            </div>

            <div className="col-12 mb-4">
              <div className="form-group">
                <label className="form-label">
                  <i className="pi pi-verified me-1"></i>
                  Certificate Reference
                </label>
                <InputText
                  name="certificate"
                  value={inspectionFormData.certificate}
                  onChange={(e) =>
                    setInspectionFormData({
                      ...inspectionFormData,
                      certificate: e.target.value,
                    })
                  }
                  required
                  className="form-control"
                  placeholder="e.g., CERT-2025-001, INS-240815-A"
                />
                <small className="text-muted">
                  Enter certificate number or reference code
                </small>
              </div>
            </div>
          </div>

          <Divider />

          <div className="d-flex justify-content-end gap-2 pt-3">
            <Button
              type="button"
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowInspectionModal(false)}
              className="p-button-outlined p-button-secondary"
            />
            <Button
              type="submit"
              label="Add Certificate"
              icon="pi pi-verified"
              loading={loading}
              className="p-button-warning"
            />
          </div>
        </form>
      </Dialog>
    </Layout>
  );
};

export default AssetManagement;
